import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Busca_GeralResponse';

import type { Busca_GeralRow } from '../contracts/Busca_GeralRow';

export const GLOBAL_SEARCH_MIN_TERM_LENGTH = 2;
const DEBOUNCE_MS = 300;

/**
 * System-wide search (lupa / ⌘B): clientes, orçamentos, compromissos and ordens
 * de serviço through GET /busca/geral. Debounced, >= 2 chars, stale responses
 * are dropped so only the latest term's result is applied.
 */
export function useGlobalSearch() {
  const { t } = useI18n();
  const results = ref<Busca_GeralRow[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let latestRequestId = 0;

  const doSearch = async (term: string, requestId: number): Promise<void> => {
    log('useGlobalSearch', 'doSearch', 'Searching', { term });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get('/index.php/busca/geral', {
        params: { q: term },
        responseContract: 'Busca_GeralResponse',
      });
      if (requestId !== latestRequestId) return;
      if (response.success) {
        results.value = response.dados ?? [];
      } else {
        throw new Error(response.error || t('Erro ao buscar'));
      }
    } catch (err) {
      if (requestId !== latestRequestId) return;
      logError('useGlobalSearch', 'doSearch', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      if (requestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  };

  const search = (term: string): void => {
    const trimmed = term.trim();
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (trimmed.length < GLOBAL_SEARCH_MIN_TERM_LENGTH) {
      latestRequestId += 1;
      results.value = [];
      isLoading.value = false;
      error.value = null;
      return;
    }
    const requestId = ++latestRequestId;
    debounceTimer = setTimeout(() => {
      void doSearch(trimmed, requestId);
    }, DEBOUNCE_MS);
  };

  const clear = (): void => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    latestRequestId += 1;
    results.value = [];
    isLoading.value = false;
    error.value = null;
  };

  return { results, isLoading, error, search, clear };
}
